import Image from '@jobseeker/lotus/components/Image';
import {
  CEO,
  HRBP,
  Marketing,
  ProductManager,
  Target,
} from '@jobseeker/lotus/assets/icons';
import {Box, Button, TextField, Typography, useTheme} from '@mui/material';
import {useRouter} from 'next/router';
import {useEffect, useRef, useState} from 'react';

const suggestions = [
  {
    iconName: 'product',
    label:
      'Seeking mid-level Product Manager role in delhi/ncr with 5 yrs experience',
  },
  {
    iconName: 'marketing',
    label: 'Exploring Head of Marketing roles in mumbai or bangalore',
  },
  {
    iconName: 'ceo',
    label: 'Looking for Chief of Staff positions in bangalore/delhi ncr/mumbai',
  },
  {
    iconName: 'hrbp',
    label:
      'Exploring HRBP opportunities in Bangalore with 4–6 yrs of HR experience',
  },
  {
    iconName: 'target',
    label:
      'Seeking Category Manager opportunities in retail or online marketplaces',
  },
];

interface FreeTextSearchBarProps {
  onSubmit?: (query: string) => void;
  onReset?: (userQuery?: string, section?: string) => void;
  initialInput?: string;
  useOverlay?: boolean;
  onInputChange?: (value: string) => void;
}

const FreeTextSearchBar = ({
  onSubmit,
  onReset,
  initialInput = '',
  useOverlay = true,
  onInputChange,
}: FreeTextSearchBarProps) => {
  const [input, setInput] = useState(initialInput);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isMediumScreen, setIsMediumScreen] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pathname = router.asPath;
  const isAISearchRoute = pathname.startsWith('/ai-search');
  const theme = useTheme();
  const handleSearch = () => {
    if (onSubmit) onSubmit(input);
  };

  const handleReset = () => {
    setInput('');
    onInputChange?.('');
    inputRef.current?.querySelector('textarea')?.focus();
    if (onReset) onReset(input, 'Reset');
  };

  const getIcons = (iconType: string) => {
    switch (iconType) {
      case 'product':
        return ProductManager;
      case 'marketing':
        return Marketing;
      case 'ceo':
        return CEO;
      case 'hrbp':
        return HRBP;
      case 'target':
        return Target;
      default:
        return 'product';
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 900);
      setIsMediumScreen(window.innerWidth > 500 && window.innerWidth < 1200);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 900);
      setIsMediumScreen(window.innerWidth > 900 && window.innerWidth < 1200);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Box sx={{position: 'relative'}}>
      <Box
        width={'100%'}
        height={{
          xs: '178.15px',
          md: '130px',
        }}
        borderRadius={4}
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'space-between'}
        borderTop={`${theme.spacing(0.25)} solid transparent`}
        borderLeft={`${theme.spacing(0.25)} solid transparent`}
        borderRight={`${theme.spacing(0.25)} solid transparent`}
        borderBottom={`${theme.spacing(0.5)} solid transparent`}
        sx={{
          background:
            'linear-gradient(#fff, #fff) padding-box, linear-gradient(270deg, #7E9F67 0%, #C1D1B5 66.1%) border-box',
        }}
      >
        <Box marginTop={2} marginLeft={2} marginRight={3}>
          <TextField
            ref={inputRef}
            value={input}
            onKeyDown={e => {
              if (e.key === 'Enter' && input.trim()) {
                e.preventDefault();
                inputRef.current?.querySelector('textarea')?.blur();
                handleSearch();
              }
            }}
            onChange={e => {
              const newValue = e.target.value.slice(0, 200);
              setInput(newValue);
              onInputChange?.(newValue);
            }}
            onFocus={() => setShowOverlay(true)}
            onBlur={e => {
              setTimeout(() => setShowOverlay(false), 150);
            }}
            minRows={2}
            maxRows={isSmallScreen ? 4 : 2}
            multiline
            placeholder={
              showOverlay
                ? ''
                : `Describe the job you're looking for:\n"I am looking for a senior sales & Marketing role in Delhi with 8 years of experience in a startup"`
            }
            sx={{
              height: '42px',
              width: '100%',
              fontWeight: '500',
              fontSize: theme.typography.pxToRem(14),
              lineHeight: theme.typography.pxToRem(20),
              color: '#333333',
              '& .MuiOutlinedInput-root': {
                padding: 0,
                '& textarea': {
                  fontSize: theme.typography.pxToRem(14),
                  lineHeight: theme.typography.pxToRem(20),
                  padding: 0,
                  '&::placeholder': {
                    fontSize: theme.typography.pxToRem(12),
                    lineHeight: theme.typography.pxToRem(18),
                  },
                },
                '& fieldset': {borderColor: 'transparent'},
                '&:hover fieldset': {borderColor: 'transparent'},
                '&.Mui-focused fieldset': {borderColor: 'transparent'},
              },
            }}
          />
        </Box>

        <Box
          ml={2}
          mb={theme.spacing(1.5)}
          display={'flex'}
          justifyContent={'flex-start'}
          alignItems={'center'}
        >
          <Button
            onClick={handleSearch}
            disabled={!input.trim() || input.length >= 200}
            sx={{
              width: '82px',
              height: '30px',
              borderRadius: theme.spacing(4),
              fontSize: theme.typography.pxToRem(14),
              lineHeight: theme.typography.pxToRem(18),
              fontWeight: '400',
              cursor:
                input.trim() && input.length < 200 ? 'pointer' : 'not-allowed',
              bgcolor:
                input.trim() && input.length < 200 ? '#000' : '#00000029',
              color: input.trim() && input.length < 200 ? '#fff' : '#00000042',
              '&:hover': {
                bgcolor:
                  input.trim() && input.length < 200 ? '#000' : '#00000029',
              },
            }}
          >
            Search
          </Button>
          <Typography
            variant="subtitle2"
            onClick={handleReset}
            aria-label="Reset"
            marginLeft={2}
            lineHeight={theme.typography.pxToRem(16)}
            sx={{
              cursor: input.trim() ? 'pointer' : 'not-allowed',
              color: input.trim() ? '#49966E' : 'rgba(0, 0, 0, 0.26)',
            }}
          >
            Clear Text
          </Typography>
        </Box>
      </Box>

      {!isSmallScreen && showOverlay && (
        <Box
          onMouseDown={e => {
            e.preventDefault();
          }}
          width={'100%'}
          color={'#FFFFFF'}
          borderRadius={theme.spacing(2)}
          boxShadow={`0px ${theme.spacing(1.25)} ${theme.spacing(
            1.25
          )} -${theme.spacing(0.625)} #0000001F`}
          position={'absolute'}
          zIndex={100}
          marginTop={theme.spacing(0.5)}
          display={'block'}
          border={`${theme.spacing(0.25)} solid #F3F7FA`}
          overflow={'auto'}
          visibility={'visible'}
          padding={theme.spacing(2)}
          sx={{
            position: 'absolute',
            backgroundColor: '#fff',
            zIndex: '100',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          <Typography
            variant="subtitle1"
            lineHeight={theme.typography.pxToRem(24)}
            color={'#333333'}
            height={'24px'}
          >
            Popular Searches
          </Typography>

          <Box
            mt={theme.spacing(1.25)}
            display={'grid'}
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            }}
            width={'100%'}
            gap={theme.spacing(1.5)}
            sx={{
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
                width: 0,
                height: 0,
              },
              '&::-webkit-scrollbar-track': {
                display: 'none',
              },
              '&::-webkit-scrollbar-thumb': {
                display: 'none',
              },
            }}
          >
            {suggestions?.map((suggestion, index) => (
              <Box
                onClick={() => {
                  if (isAISearchRoute) {
                    setInput(suggestion?.label);
                    onInputChange?.(suggestion?.label);
                    setShowOverlay(false);
                    const textarea =
                      inputRef.current?.querySelector('textarea');
                    textarea?.blur();
                  }
                  onSubmit?.(suggestion?.label);
                }}
                key={index}
                border={`${theme.spacing(0.1)} solid`}
                borderRadius={theme.spacing(4)}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'flex-start'}
                gap={theme.spacing(1.25)}
                py={theme.spacing(1.25)}
                px={theme.spacing(1.75)}
                minWidth={'max-content'}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: '#F3F7FA',
                  '&:hover': {
                    border: `${theme.spacing(0.1)} solid #6B8A55`,
                    '& .suggestion-text': {
                      background:
                        'linear-gradient(90deg, #225448 0%, #7E9F67 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    },
                  },
                }}
              >
                <Image
                  className="verified_recruiter_tag"
                  src={getIcons(suggestion?.iconName)}
                  alt={'premium_icon'}
                  width={16}
                  height={16}
                  style={{
                    objectFit: 'cover',
                  }}
                />

                <Typography
                  className="suggestion-text"
                  variant="subtitle3"
                  lineHeight={theme.typography.pxToRem(14)}
                  color={'#333333'}
                  whiteSpace={'nowrap'}
                >
                  {suggestion?.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {input?.length >= 200 && (
        <Typography
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          mt={1}
          variant="subtitle3"
          color={'#D11124'}
        >
          200 characters limit
        </Typography>
      )}
    </Box>
  );
};

export default FreeTextSearchBar;
